<template lang="pug">
div.wrap-gltf-uploader.relative.w-full.h-full
  div(v-show="!mainImg").wrap-studio.relative.w-full.h-full.overflow-hidden.rounded   
    div.wrap-canvas
      div(ref="wrapCanvas")
    div(v-if="loadingProgress == 100 && isInitialized"
      ).wrap-params-changer.absolute.w-full.px12
      //- v-slider(v-model="ambientLightIntensity"
        label="明るさ" min="0" max="20" step="0.1")
      v-slider(v-model="cameraFov"
        label="画角　" min="0" max="200" step="0.1")
      v-slider(v-model="emissiveColor"
        label="輝度　" :min="0x000000" :max="0xffffff" :step="0xffffff / 15")
      v-slider(v-model="lineSegmentsThrehold"
        :disabled="isCreatingLineGroup"
        @change="setNewLineSegments"
        label="線密度" min="0" max="99" step="1")
    div(v-if="loadingProgress != 100").wrap-loading.f.fh.w-full.h-full.l0.t0
      div
        div.f.fc.mb8
          v-progress-circular(indeterminate)
        span.block.text-center.mb12 {{Math.floor(loadingProgress)}}%
        span(v-if="!isEditPage").block.text-center.fz14 3Dオブジェクトのサムネイルを撮影しましょう！
    div(v-if="loadingProgress == 100 && isInitialized"
      @click="onTakePhoto").btn.absolute.t8.l8.f.fm.px10.py8.rounded.cursor-pointer
      v-icon(v-if="!isUploading" dark).mr6 mdi-camera
      v-progress-circular(v-else indeterminate size="16" width="2" color="white").mr6
      span.fz14.bold.mt2 撮影する
  div(v-if="mainImg && isInitialized").wrap-photo-img.relative.h-full.overflow-hidden.rounded
    div(@click="onRetakePhoto").btn.absolute.t8.l8.f.fm.px10.py8.rounded.cursor-pointer
      v-icon(dark).mr6 mdi-camera-retake
      span.fz14.bold.mt2 取り直す
    div.f.fh.h-full
      img(:src="mainImg").object-fit-cover.w-full.h-full
</template>

<style lang="scss" scoped>
@import "@/scss/_variables.scss";
$basic_margin: 12px;
.wrap-gltf-uploader {
  // border: $basic_border;
  .wrap-studio {
    .wrap-params-changer {
      bottom: $basic_margin;
      background: rgba(255, 255, 255, 0.4);
    }
  }
  .wrap-studio, .wrap-photo-img {
    border: $basic_border_bold;
    .btn {
      background: $active_color;
      span {
        color: $dark_primary_text_color;
      }
    }
  }
}
</style>

<style lang="scss">
@import "@/scss/_variables.scss";
.wrap-gltf-uploader {
  .v-input__slot {
    margin-bottom: 0;
  }
  .v-messages {
    display: none;
  }
}
</style>

<script>
import { storage } from '@/components/utils/firebase'
import { createNamespacedHelpers } from 'vuex'
const { mapState: mapStateAuth } = createNamespacedHelpers('auth')
import studioManager from '@/components/mixin/studioManager'
import gltfManager from '@/components/mixin/gltfManager'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'

export default {
  mixins: [studioManager, gltfManager],
  props: {
    gltfPath: {
      type: String,
      required: true
    },
    defaultAsset: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      renderer: null, scene: null, clock: null,
      camera: null, cameraFov: 35,
      orbitControls: null,
      ambientLight: null, ambientLightIntensity: 1,
      emissiveColor: 0x666666,
      gltf: null,
      lineGroup: null, lineSegmentsThrehold: 10, isCreatingLineGroup: false,
      loadingProgress: 0,
      canvasSize: null,
      onResizeTimer: null,
      mainImg: null,
      isUploading: false,
      positionOffset: null, originalInitialSize: null,
      isInitialized: false
    }
  },
  computed: {
    ...mapStateAuth(['uid']),
    edgeDegreeThrehold () {
      return 100 - this.lineSegmentsThrehold
    },
    needsLineGroup () {
      return this.lineSegmentsThrehold > 0 && this.edgeDegreeThrehold > 0
    },
    isEditPage () {
      return this.$route.name === 'edit-asset'
    }
  },
  watch: {
    // ambientLightIntensity () {
    //   this.ambientLight.intensity = this.ambientLightIntensity
    // },
    emissiveColor () {
      this.updateMaterialEmissive({
        object: this.gltf.scene,
        emissiveColor: this.emissiveColor
      })
    },
    cameraFov () {
      this.camera.setFocalLength(this.cameraFov)
    }
  },
  async mounted () {
    window.addEventListener('resize', () => {
      if (this.onResizeTimer) clearTimeout(this.onResizeTimer)
      this.onResizeTimer = setTimeout(this.onResize, 400)
    })
    this.canvasSize = {
      width: this.$el.clientWidth,
      height: this.$el.clientHeight
    }
    await this.initializeScene()
    if (this.defaultAsset) this.mainImg = this.defaultAsset.mainImg
    this.isInitialized = true
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onResize)
    this.disposeScene({ renderer: this.renderer, scene: this.scene })
  },
  methods: {
    async initializeScene () {
      this.scene = new THREE.Scene()
      this.clock = new THREE.Clock()

      // レンダラーの設定
      this.renderer = this.createRenderer({ 
        size: { width: this.canvasSize.width, height: this.canvasSize.height }
      })
      this.$refs.wrapCanvas.appendChild(this.renderer.domElement)

      // 座標軸を表示
      // const axesHelper = new THREE.AxesHelper(0.5)
      // this.scene.add(axesHelper)

      // カメラの設定
      this.camera = new THREE
        .PerspectiveCamera(this.cameraFov, this.canvasSize.width / this.canvasSize.height, 0.1, 1000)

      if (this.defaultAsset && this.defaultAsset.camera) {
        const vec3Pos = this.defaultAsset.camera.position
        this.camera.position.set(vec3Pos.x, vec3Pos.y, vec3Pos.z)
      } else {
        this.camera.position.set(0, 0.0, 3.5)
      }

      // PostProcessing by mixim
      this.initializeComposer({
        renderer: this.renderer, scene: this.scene, camera: this.camera
      })

      // カメラコントーロールの設定
      this.orbitControls = new OrbitControls(this.camera, this.renderer.domElement)
      if (this.defaultAsset && this.defaultAsset.defaultOrbitControls) {
        const vec3Target = this.defaultAsset.defaultOrbitControls.target
        this.orbitControls.target0.set(vec3Target.x, vec3Target.y, vec3Target.z)
      } else {
        this.orbitControls.target.set(0, 0, 0)
      }
      this.orbitControls.screenSpacePanning = true
      this.orbitControls.update()

      // ライトの設定
      const light = new THREE.DirectionalLight(0xffffff)
      light.position.set(1, 10, 1).normalize()
      light.intensity = this.defaultDirectionalLightIntensity
      this.scene.add(light)

      this.ambientLight = new THREE.AmbientLight(0xffffff)
      this.ambientLight.intensity = this.defaultAmbientLightIntensity
      this.ambientLightIntensity = this.defaultAmbientLightIntensity
      this.scene.add(this.ambientLight)

      // gltfのロード
      try {
        const progressFunc = (progress) => {
          this.loadingProgress = 100.0 * (progress.loaded / progress.total)
        }
        const errorFunc = (error) => {
          alert(error.type)
          console.error(error)
          this.$emit('onReset')
        }
        this.gltf = await this.loadGLTFwithDracoLoader({ 
          path: this.gltfPath, progressFunc, errorFunc
        })
        // this.gltf.scene = this.mergeObject(this.gltf.scene)
        this.gltf.scene = this.mergeObjectByModelSize({ object: this.gltf.scene })
        this.gltf.scene.traverse(node => node.applyOutline = true)

        // const gltfModel = gltf.scene
        // gltfModel.traverse((object) => { //モデルの構成要素
        //   if (object.isMesh) { //その構成要素がメッシュだったら
        //     object.material.trasparent = true //透明許可
        //     object.material.opacity = 0.8 //透過
        //     object.material.depthTest = true //陰影で消える部分
        //   }
        // })

        // 3dモデルに線画を追加
        // this.lineGroup = this.createLineGroupAttachedTo3DModel({
        //   sceneObject: this.gltf.scene, thresholdAngle: this.lineSegmentsThrehold
        // })
        // this.gltf.scene.userData.lineGroupUuid = this.lineGroup.uuid

        // const edgeDegreeThrehold = 100 - this.lineSegmentsThrehold
        // if (edgeDegreeThrehold > 0) {
        if (this.edgeDegreeThrehold > 0) {
          this.lineGroup = this.createLineGeometoryToGroup({
            group: this.gltf.scene, edgeDegreeThrehold: (100 - this.lineSegmentsThrehold)
          })
          this.gltf.scene.userData.lineGroupUuid = this.gltf.scene.uuid
        }

        // 初期サイズの設定
        const box3 = new THREE.Box3().setFromObject(this.gltf.scene)
        this.originalInitialSize = {
          w: box3.max.x - box3.min.x, h: box3.max.y - box3.min.y
        }
        
        const wSize = box3.max.x - box3.min.x
        const initialScale = (1.0 / wSize) * 2.0 // 1だと小さすぎるのでサイズを2.0しておく
        this.gltf.scene.scale.set(initialScale, initialScale, initialScale)

        // this.gltf.scene.position.set(0, 0, 0)
        // モデルのオフセットを設定
        this.positionOffset = box3.getCenter(new THREE.Vector3())
        this.gltf.scene.position.set(
          -this.positionOffset.x * initialScale,
          -this.positionOffset.y * initialScale,
          -this.positionOffset.z * initialScale
        )

        // lineGroupのスケールはgltfのスケールが設定されてから合わせないとbox3の関係で大きさがずれる
        if (this.lineGroup) {
          this.lineGroup.position.copy(this.gltf.scene.position)
          this.lineGroup.scale.copy(this.gltf.scene.scale)
          this.lineGroup.rotation.copy(this.gltf.scene.rotation)
          this.scene.add(this.lineGroup)
        }

        this.scene.add(this.gltf.scene)
      } catch (error) {
        console.log('error:', error)
        this.$root.$emit('showNavigationMessage', {
          message: 'このモデルは利用できません。', showOK: true
        })
        this.$emit('onReset')
      }
      
      // アニメーションを開始
      this.tick()
    },
    tick () {
      requestAnimationFrame(this.tick)
      this.composer.render(this.scene, this.camera)
      // this.renderer.render(this.scene, this.camera)
    },
    async setNewLineSegments () {
      // const edgeDegreeThrehold = 100 - this.lineSegmentsThrehold
      // if (edgeDegreeThrehold <= 0) {
      if (!this.needsLineGroup) {
        if (this.lineGroup) {
          this.scene.remove(this.lineGroup)
          delete this.gltf.scene.userData.lineGroupUuid
          this.lineGroup = null
        }
        return
      }

      this.isCreatingLineGroup = true
      await this.sleep(100)

      if (this.lineGroup) this.scene.remove(this.lineGroup)

      this.lineGroup = this.createLineGeometoryToGroup({
        group: this.gltf.scene, edgeDegreeThrehold: this.edgeDegreeThrehold
      })
      this.gltf.scene.userData.lineGroupUuid = this.gltf.scene.uuid

      this.scene.add(this.lineGroup)

      this.isCreatingLineGroup = false
    },
    onResize () {
      if (this.onResizeTimer) clearTimeout(this.onResizeTimer)
      this.onResizeTimer = setTimeout(this.resizeCanvas, 400)
    },
    resizeCanvas () {
      this.canvasSize = {
        width: this.$el.clientWidth,
        height: this.$el.clientHeight
      }
      if (this.renderer && this.camera) {
        // カメラのアスペクト比を正す
        this.camera.aspect = this.canvasSize.width / this.canvasSize.height
        this.camera.updateProjectionMatrix()

        this.renderer.setPixelRatio(window.devicePixelRatio)
        this.renderer.setSize(this.canvasSize.width, this.canvasSize.height)
        this.resizeComposer({ 
          canvasSize: this.canvasSize, renderer: this.renderer
        })
        // this.resizeComposer(this.canvasSize)
      }
    },
    getCameraParams () {
      return {
        position: this.toPosition(this.camera.position),
        zoom: this.camera.zoom,
        fov: this.camera.fov
      }
    },
    getOrbitControlsParams () {
      return {
        position: this.toPosition(this.orbitControls.position0),
        target: this.toPosition(this.orbitControls.target)
      }
    },
    async onTakePhoto () {
      this.isUploading = true
      try {
        const wrapCanvas = this.$refs.wrapCanvas
        const canvas = wrapCanvas.getElementsByTagName('canvas')[0]
        const base64 = canvas.toDataURL()
        const storageRef = storage.ref()
        const fileRef = storageRef.child(`/ASSET/${this.uid}/IMG/${this.getUniqueStr()}.png`)
        const downloadURL = await fileRef.putString(base64.split(',')[1], 'base64')
          .then(snapshot => {
            return snapshot.ref.getDownloadURL().then(downloadURL => downloadURL)
          })
        this.mainImg = downloadURL
      } catch (error) {
        console.log(error)
      }

      this.isUploading = false
    },
    onRetakePhoto () {
      this.mainImg = false
    },
    getUniqueStr (myStrong) {
      let strong = 1000
      if (myStrong) strong = myStrong
      return new Date().getTime().toString(16)  + Math.floor(strong*Math.random()).toString(16)
    }
  }
}
</script>
